import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SchoolIcon from '@mui/icons-material/School';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SortIcon from '@mui/icons-material/Sort';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import useAuthStore from '../../store/authStore';
import { fetchTutorStudents, reorderStudents } from '../../api/students';

const B = { primary: '#4357AD', green: '#D3FEC6', orange: '#FF521B', dark: '#1E2A4A' };

const subjectLabels = {
  mathematics: 'Математика',
  ukrainian: 'Українська мова',
  chemistry: 'Хімія',
  biology: 'Біологія',
  physics: 'Фізика',
  history: 'Історія',
};
const translateSubject = (val) => subjectLabels[val?.toLowerCase()] || val || '';

const avatarColors = ['#4357AD', '#22C55E', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4', '#EF4444', '#14B8A6'];

const SORT_OPTIONS = [
  { value: 'custom', label: 'Мій порядок' },
  { value: 'date', label: 'За датою заняття' },
  { value: 'name', label: 'За алфавітом' },
  { value: 'lessons', label: 'За к-стю занять' },
  { value: 'incomplete', label: 'Незаповнені' },
  { value: 'grade', label: 'За класом' },
];

const sortStudents = (students, sortBy) => {
  if (sortBy === 'custom' || !sortBy) return students;
  const sorted = [...students];
  switch (sortBy) {
    case 'date':
      return sorted.sort((a, b) => {
        if (!a.lastLessonDate && !b.lastLessonDate) return 0;
        if (!a.lastLessonDate) return 1;
        if (!b.lastLessonDate) return -1;
        return dayjs(b.lastLessonDate).valueOf() - dayjs(a.lastLessonDate).valueOf();
      });
    case 'name':
      return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'uk'));
    case 'lessons':
      return sorted.sort((a, b) => (b.totalLessons || 0) - (a.totalLessons || 0));
    case 'incomplete':
      return sorted.filter((s) => (s.incompleteLessons || 0) > 0);
    case 'grade':
      return sorted.sort((a, b) => {
        const ga = parseInt(a.grade) || 0;
        const gb = parseInt(b.grade) || 0;
        return ga - gb;
      });
    default:
      return sorted;
  }
};

const ITEM_TYPE = 'STUDENT_CARD';

const DraggableStudentCard = ({ student, index, color, moveCard, onNavigate, isDragEnabled }) => {
  const ref = useRef(null);
  const hasIncomplete = student.incompleteLessons > 0;
  const hasLastLesson = Boolean(student.lastLessonDate);
  const hasNotes = Boolean(student.lastNotes);

  const [{ isDragging }, drag, preview] = useDrag({
    type: ITEM_TYPE,
    item: { index },
    canDrag: isDragEnabled,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    hover(item) {
      if (!isDragEnabled) return;
      if (item.index === index) return;
      moveCard(item.index, index);
      item.index = index;
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  preview(drop(ref));

  return (
    <Box ref={ref} sx={{ minWidth: 0, height: '100%' }}>
      <Card
        sx={{
          borderRadius: 4,
          boxShadow: isDragging
            ? '0 12px 40px rgba(67,87,173,0.25)'
            : '0 2px 12px rgba(67,87,173,0.08)',
          bgcolor: '#fff',
          border: isOver
            ? `2px solid ${B.primary}`
            : '1.5px solid #EDE9FE',
          transition: 'box-shadow 0.2s, transform 0.2s, border 0.15s',
          opacity: isDragging ? 0.6 : 1,
          transform: isDragging ? 'scale(1.02)' : 'none',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(67,87,173,0.15)',
            transform: 'translateY(-3px)',
          },
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          minWidth: 0,
          height: '100%',
        }}
      >
        {/* Drag handle — absolute, top-left, above CardActionArea */}
        {isDragEnabled && (
          <Box
            ref={drag}
            sx={{
              position: 'absolute',
              top: 14,
              left: 10,
              zIndex: 2,
              cursor: 'grab',
              color: '#C4B5FD',
              display: 'flex',
              alignItems: 'center',
              p: 0.5,
              borderRadius: 1,
              '&:hover': { color: B.primary, bgcolor: 'rgba(67,87,173,0.06)' },
              '&:active': { cursor: 'grabbing' },
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <DragIndicatorIcon sx={{ fontSize: 20 }} />
          </Box>
        )}

        {/* Top color accent bar */}
        <Box sx={{ height: 4, bgcolor: B.primary, borderRadius: '16px 16px 0 0' }} />

        <CardActionArea
          onClick={() => onNavigate(student.id)}
          sx={{ borderRadius: '0 0 16px 16px', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
        >
          <CardContent sx={{ py: 2.5, px: 3, pl: isDragEnabled ? 5 : 3, flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
            {/* Header: avatar + name + grade */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  hasIncomplete ? (
                    <Tooltip title={`Незаповнених уроків: ${student.incompleteLessons}`}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          bgcolor: '#EF4444',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid #fff',
                          boxShadow: '0 2px 6px rgba(239,68,68,0.3)',
                        }}
                      >
                        {student.incompleteLessons}
                      </Box>
                    </Tooltip>
                  ) : null
                }
              >
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: `${color}15`,
                    color: color,
                    fontWeight: 700,
                    fontSize: 18,
                    border: `2px solid ${color}30`,
                  }}
                >
                  {student.name?.charAt(0)?.toUpperCase()}
                </Avatar>
              </Badge>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: B.dark, lineHeight: 1.3 }} noWrap>
                  {student.name}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <Chip
                    icon={<SchoolIcon sx={{ fontSize: 14 }} />}
                    label={student.grade}
                    size="small"
                    sx={{
                      bgcolor: '#EDE9FE',
                      color: B.primary,
                      fontWeight: 600,
                      fontSize: 12,
                      height: 24,
                      '& .MuiChip-icon': { color: B.primary },
                    }}
                  />
                  {student.subjects && (
                    <Typography variant="caption" sx={{ color: '#8891A5', fontWeight: 500 }} noWrap>
                      {translateSubject(student.subjects)}
                    </Typography>
                  )}
                </Stack>
              </Box>

              <ArrowForwardIcon sx={{ color: '#C4B5FD', fontSize: 22, flexShrink: 0 }} />
            </Stack>

            {/* Last lesson section — always visible */}
            <Box
              sx={{
                bgcolor: '#F8FAFC',
                borderRadius: 2.5,
                px: 2,
                py: 1.5,
                mb: 1.5,
                border: '1px solid #F1F5F9',
                minHeight: 56,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                overflow: 'hidden',
                minWidth: 0,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: hasLastLesson && student.lastLessonTopic ? 0.5 : 0 }}>
                <CalendarTodayIcon sx={{ fontSize: 14, color: hasLastLesson ? B.primary : '#C4B5FD' }} />
                <Typography variant="caption" fontWeight={600} sx={{ color: hasLastLesson ? B.primary : '#8891A5' }}>
                  Останнє заняття
                </Typography>
                {hasLastLesson && (
                  <Typography variant="caption" sx={{ color: '#8891A5', ml: 'auto !important' }}>
                    {dayjs(student.lastLessonDate).format('DD.MM.YYYY')}
                  </Typography>
                )}
              </Stack>
              {hasLastLesson && student.lastLessonTopic ? (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    color: B.dark,
                    fontWeight: 500,
                    fontSize: 13,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {student.lastLessonTopic}
                </Typography>
              ) : !hasLastLesson && (
                <Typography variant="caption" sx={{ color: '#B0B8C9' }}>
                  Занять ще немає
                </Typography>
              )}
            </Box>

            {/* Notes section — always visible */}
            <Box
              sx={{
                bgcolor: '#FAFAFA',
                borderRadius: 2.5,
                px: 2,
                py: 1.5,
                border: '1px solid #F1F5F9',
                minHeight: 56,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                overflow: 'hidden',
                minWidth: 0,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: hasNotes ? 0.5 : 0 }}>
                <StickyNote2OutlinedIcon sx={{ fontSize: 14, color: '#C4B5FD' }} />
                <Typography variant="caption" fontWeight={600} sx={{ color: '#8891A5' }}>
                  Примітки
                </Typography>
              </Stack>
              {hasNotes ? (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    color: B.dark,
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {student.lastNotes}
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ color: '#B0B8C9' }}>
                  Примітки відсутні
                </Typography>
              )}
            </Box>

            {/* Warning chip at bottom — fixed height container */}
            <Box sx={{ mt: 1.5, minHeight: 26 }}>
              {hasIncomplete && (
                <Chip
                  icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                  label={`${student.incompleteLessons} незаповнен${student.incompleteLessons === 1 ? 'ий урок' : 'их уроків'}`}
                  size="small"
                  sx={{
                    bgcolor: '#FEF3C7',
                    color: '#92400E',
                    fontWeight: 600,
                    fontSize: 11.5,
                    height: 26,
                    '& .MuiChip-icon': { color: '#D97706' },
                  }}
                />
              )}
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </Box>
  );
};

const TutorStudentsPage = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [localOrder, setLocalOrder] = useState(null);
  const [sortBy, setSortBy] = useState('custom');

  const { data, isLoading } = useQuery({
    queryKey: ['tutor-students', user?.id],
    queryFn: () => fetchTutorStudents(user.id),
    enabled: Boolean(user?.id),
    onSuccess: () => setLocalOrder(null),
  });

  const reorderMutation = useMutation({
    mutationFn: (studentIds) => reorderStudents(user.id, studentIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tutor-students'] });
    },
  });

  const reorderTimerRef = useRef(null);

  // Cleanup debounce timer on unmount
  useEffect(() => () => {
    if (reorderTimerRef.current) clearTimeout(reorderTimerRef.current);
  }, []);

  const baseStudents = localOrder || data || [];
  const students = useMemo(() => sortStudents(baseStudents, sortBy), [baseStudents, sortBy]);
  const hasStudents = baseStudents.length > 0;
  const isFiltered = hasStudents && students.length === 0;
  const totalIncomplete = baseStudents.reduce((sum, s) => sum + (s.incompleteLessons || 0), 0);
  const isDragEnabled = sortBy === 'custom';

  const moveCard = useCallback(
    (fromIndex, toIndex) => {
      const updated = [...(localOrder || data || [])];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      setLocalOrder(updated);

      // Debounce: save new order to backend after user stops dragging
      if (reorderTimerRef.current) clearTimeout(reorderTimerRef.current);
      reorderTimerRef.current = setTimeout(() => {
        const ids = updated.map((s) => s.id);
        reorderMutation.mutate(ids);
      }, 500);
    },
    [localOrder, data, reorderMutation]
  );

  const handleNavigate = useCallback(
    (studentId) => navigate(`/tutor/students/${studentId}`),
    [navigate]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: B.primary }} />
      </Box>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Stack spacing={2.5}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ color: B.dark }}>
              Мої учні
            </Typography>
            <Typography variant="body1" sx={{ color: '#8891A5', mt: 0.5 }}>
              {baseStudents.length} {baseStudents.length === 1 ? 'учень' : 'учнів'}
            </Typography>
          </Box>
          {totalIncomplete > 0 && (
            <Chip
              icon={<WarningAmberIcon sx={{ fontSize: 18 }} />}
              label={`${totalIncomplete} незаповнен${totalIncomplete === 1 ? 'ий урок' : 'их уроків'}`}
              sx={{
                bgcolor: '#FFFBEB',
                color: '#92400E',
                border: '1px solid #FDE68A',
                fontWeight: 600,
                fontSize: 13,
                '& .MuiChip-icon': { color: '#D97706' },
              }}
            />
          )}
        </Box>

        {/* Sort controls */}
        {hasStudents && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <SortIcon sx={{ fontSize: 18, color: '#8891A5', display: { xs: 'none', sm: 'block' } }} />
            <ToggleButtonGroup
              value={sortBy}
              exclusive
              onChange={(_, val) => { if (val) { setSortBy(val); if (val !== 'custom') setLocalOrder(null); } }}
              size="small"
              sx={{
                flexWrap: 'wrap',
                gap: 0.5,
                '& .MuiToggleButton-root': {
                  border: '1px solid #EDE9FE',
                  borderRadius: '20px !important',
                  px: { xs: 1.5, sm: 2 },
                  py: 0.5,
                  textTransform: 'none',
                  fontSize: { xs: 11, sm: 12.5 },
                  fontWeight: 600,
                  color: '#8891A5',
                  '&.Mui-selected': {
                    bgcolor: B.primary,
                    color: '#fff',
                    borderColor: B.primary,
                    '&:hover': { bgcolor: '#3648A0' },
                  },
                  '&:hover': { bgcolor: '#F5F3FF' },
                },
              }}
            >
              {SORT_OPTIONS.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Empty state */}
        {!hasStudents ? (
          <Card sx={{ borderRadius: 4, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', bgcolor: '#fff' }}>
            <CardContent sx={{ py: 6, textAlign: 'center' }}>
              <PersonIcon sx={{ fontSize: 48, color: '#C4B5FD', mb: 1 }} />
              <Typography variant="h6" sx={{ color: B.dark, mb: 0.5 }}>Учнів поки немає</Typography>
              <Typography variant="body2" sx={{ color: '#8891A5' }}>Зверніться до адміністратора для додавання учнів.</Typography>
            </CardContent>
          </Card>
        ) : isFiltered ? (
          <Card sx={{ borderRadius: 4, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', bgcolor: '#fff' }}>
            <CardContent sx={{ py: 6, textAlign: 'center' }}>
              <PersonIcon sx={{ fontSize: 48, color: '#C4B5FD', mb: 1 }} />
              <Typography variant="h6" sx={{ color: B.dark, mb: 0.5 }}>Немає учнів за заданими критеріями</Typography>
              <Typography variant="body2" sx={{ color: '#8891A5' }}>Спробуйте інший варіант сортування.</Typography>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2.5 }}>
            {students.map((student, i) => (
              <DraggableStudentCard
                key={student.id}
                student={student}
                index={i}
                color={avatarColors[i % avatarColors.length]}
                moveCard={moveCard}
                onNavigate={handleNavigate}
                isDragEnabled={isDragEnabled}
              />
            ))}
          </Box>
        )}
      </Stack>
    </DndProvider>
  );
};

export default TutorStudentsPage;
